import rehypeKatex from "rehype-katex";
import remarkMath, { type Options as MathOptions } from "remark-math";
import remarkGfm from "remark-gfm";
import { processMathFragment, splitStringByNewline } from "@/helpers/markdown.ts";
import ReactMarkdown from "react-markdown";
import type { FC } from "react";
import { assert } from "@/helpers/console.ts";
import type { FlowItem } from "@/types/flow.ts";

const remarkMathOptions: MathOptions = {
  singleDollarTextMath: false,
};

export const RemarkPlugins = [[remarkMath, remarkMathOptions], remarkGfm];
export const RehypePlugins = [rehypeKatex];

const blockElements = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "pre",
  "thead",
  "tbody",
  "tr",
  "td",
  "figure",
  "figcaption",
];

interface RenderLatexProps {
  message: string;
  flowItem?: Partial<FlowItem>;
  props?: unknown[];
}

export const RenderLatex: FC<RenderLatexProps> = ({ message }) => {
  assert(message.length !== 0, "Latex message length needs to be more than 0");

  const splitText = splitStringByNewline(processMathFragment(message));

  return (
    <>
      {splitText
        .filter((text) => text.length > 0)
        .map((text) => (
          <ReactMarkdown
            key={text}
            // @ts-expect-error
            remarkPlugins={RemarkPlugins}
            rehypePlugins={RehypePlugins}
            disallowedElements={blockElements}
            skipHtml={true}
          >
            {text}
          </ReactMarkdown>
        ))}
    </>
  );
};

// Taken from eedi-admin
export const hasSingleDollarLatex = (text: string) => {
  // Remove any double-dollar sections first so they don't false-positive
  const withoutDoubleDollar = text.replace(/\$\$[\s\S]+?\$\$/g, "");
  // Remove \(...\) and \[...\] delimited math too
  const withoutOtherDelimiters = withoutDoubleDollar
    .replace(/\\\([\s\S]+?\\\)/g, "")
    .replace(/\\\[[\s\S]+?\\]/g, "");
  // Now check for single $...$ (non-empty content, no newlines inside)
  return /\$[^$\n]+\$/.test(withoutOtherDelimiters);
};
